import {inject, injectable} from 'inversify';

import type {GoalApiModel} from '../../../../../api/goal';
import type {ApiListResponseExpanded} from '../../../../../api/types';

import {
  apiRequest,
  type ApiRpcQuery,
} from '../../../../../api/utils/apiRequest';
import {GoalMapperJrpc} from '../../../../domain/Goal/mappers/Goal.mapper.jrpc';
import {FetchUnboundGoalsQuery} from '../FetchUnboundGoals.query';

@injectable()
export class FetchUnboundGoalsQueryJrpc implements FetchUnboundGoalsQuery {
  constructor(
    @inject(GoalMapperJrpc)
    private readonly goalMapper: GoalMapperJrpc,
  ) {}

  async exec({
    courseId,
    excludeProgramId,
    programId,
    statuses,
    withCourseId = false,
  }: FetchUnboundGoalsQuery.Props): Promise<FetchUnboundGoalsQuery.Response> {
    const expand = this.goalMapper.expand.getByEnv();
    const query: ApiRpcQuery[] = [];

    if (programId) {
      query.push(['program_id', '==', programId]);
    }

    if (courseId) {
      query.push(['course_id', '==', courseId]);
    } else if (withCourseId) {
      query.push(['course_id', '!=', null]);
    } else {
      query.push(['course_id', '==', null]);
    }

    if (excludeProgramId) {
      query.push(['program_id', '!=', excludeProgramId]);
    }

    if (statuses?.length) {
      query.push(['status', 'in', statuses]);
    }

    const {_expanded, _items}: ApiListResponseExpanded<GoalApiModel> =
      await apiRequest({
        method: 'coach.goals.unbound.list',
        params: {
          expand,
          query,
          sort: [['sort_order', 1]],
        },
      });

    const expanded = _items.map<GoalMapperJrpc.ExpandedApiData>(item =>
      this.goalMapper.expand.expandObj(item, {
        expand,
        expanded: _expanded,
      }),
    );

    return expanded.map(goal => this.goalMapper.mapDataToModel(goal));
  }
}
